import { selectBalance, selectSportsbookBalance } from '~/components/account/ducks';
import type { RootState, ThunkAction } from '~/store/types';
import { AnyAction } from 'redux';
import { createSelector } from 'reselect';
import { SelectionDto } from '../../../api/sbapi/sbapi-api-model';
import bettingReducer, {
	acceptAllNewOffers as bettingAcceptAllNewOffers,
	acceptBetsChanges,
	acceptNewOffers as bettingAcceptNewOffers,
	changeSelectedBonusId as bettingChangeSelectedBonusId,
	changeStake as bettingChangeStake,
	checkBets,
	dispose as bettingDispose,
	placeBets as bettingPlaceBets,
	refreshBets,
	rejectAllNewOffers as bettingRejectAllNewOffers,
	rejectNewOffers as bettingRejectNewOffers,
	replaceBets,
	selectBetsWithData
} from './betslip-betting-duck';
import commonReducer, {
	changeAcceptMode as commonChangeAcceptMode,
	changeKeepSelections as commonChangeKeepSelections,
	changeShouldApplyBonus as commonChangeShouldApplyBonus,
	changeTab as commonChangeTab,
	dispose as commonDispose,
	expand as commonExpand,
	inititalize as commonInitialize,
	minimize as commonMinimize,
	openSettings as commonOpenSettings,
	closeSettings as commonCloseSettings,
	openShare as commonOpenShare,
	closeShare as commonCloseShare,
	selectAcceptMode as commonSelectAcceptMode,
	selectIsExpanded as commonSelectIsExpanded,
	selectIsSettingsOpen as commonSelectIsSettingsOpen,
	selectIsShareOpen as commonSelectIsShareOpen,
	selectKeepSelections as commonSelectKeepSelections,
	selectTab as commonSelectTab,
	selectShouldApplyBonus,
	selectPendingItems as commonSelectPendingItems,
	removePendingItems as commonRemovePendingItems,
	addPendingItems as commonAddPendingItems
} from './betslip-common-duck';
import dataReducer, {
	acceptItemChanges,
	addItems,
	clearItems,
	replaceItemsData as dataReplaceItemsData,
	dispose as dataDispose,
	removeItemsBySelectionIds,
	resubscribeToData,
	selectItemCount as dataSelectItemCount,
	selectItems as dataSelectItems,
	selectSelectionIds as dataSelectSelectionIds,
	selectIsSelectionInBetslip as dataSelectIsSelectionInBetslip,
	unsubscribeFromData,
	BetslipDataItemReplacement
} from './betslip-data-duck';
import { selectIsAuthenticated } from '../../../sportsbook-root-selectors';
import {
	BetBonusType,
	BetPlacementStatus,
	BetslipBetErrorType,
	BetslipLimitation,
	BetslipPendingItem,
	BetslipPersistedBet,
	BetslipPlacementStatus,
	BetslipTab
} from '../sportsbook-betslip-types';
import { betToPersistedBet, isBetAvailable } from '../sportsbook-betslip-utils';
import { isCombinationError } from '../sportsbook-betslip-error-utils';
import { betslipTabToBetTypeMap, calculateBetWithdrawStake, createItemFromSelection } from './betslip-duck-utils';
import {
	selectSportsbookValidateProductBalanceEnabled,
	selectSportsbookMaximumBetslipItems,
	selectSportsbookMaximumBetslipSystemItems
} from '~/components/brand/ducks/selectors';
import { BetslipVisibility } from '../../../sportsbook-types';
import {
	selectIsLocationAllowed,
	selectIsLocationNotShared,
	selectIsLocationPending,
	selectIsLocationDenied,
	selectIsLocationError
} from '../../location-verification/sportsbook-location-verification-duck';
import BetslipTextKeys from '../sportsbook-betslip-text-keys';
import multiply from '../../../utils/muiltiply';
import * as Api from '~/components/sportsbook/lobby/api/sportsbook-api';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
/**
 * The betslip is divided in multiple ducks.
 * This index file acts as a one-point entry to all betslip related state manipulation
 *
 * Entities:
 * Item:
 *   Items are added, removed, or toggled towards the betslip.
 *   The items hold data and are manipulated by sportsdata updation.
 *   Items state resides in the data-duck.
 * Bets:
 *   Bets are maintained to match the list of items.
 *   Bets are manipulated in the betslip by user input, and betting-api communication.
 *   Bets state resides in the betting-duck
 *
 * Moving parts:
 * index: Home to ALL externally accessible actionCreators and selectors. Orchestrates shared logic.
 * data: Home to data subscriptions and truth of item state such as removed and acceptedodds
 * betting: Home to bet related logic/state. Maintains an array of bets that can be updated, and placed.
 * common: Home to ui state and configuration.
 *
 * Dependencies:
 * index
 * - betting
 *   - data
 *   - common
 * - data
 * - common
 */

/**
 * Selects currently visible bets
 */
export const selectBets = createSelector([selectBetsWithData, commonSelectTab], (bets, tab) => {
	const betType = betslipTabToBetTypeMap[tab];
	return bets.filter(bet => bet.type === betType);
});

/**
 * Selects visible bets available to be placed
 */
export const selectBetsValidToPlace = createSelector([selectBets], bets =>
	bets.filter(bet => bet.isChecked && bet.errors.length === 0 && bet.stake > 0)
);

export const selectBetsInPlacement = createSelector([selectBets], bets =>
	bets.filter(bet =>
		[
			BetPlacementStatus.PLACING,
			BetPlacementStatus.DECLINED,
			BetPlacementStatus.UNKNOWN,
			BetPlacementStatus.PLACED,
			BetPlacementStatus.NEW_OFFER_PENDING,
			BetPlacementStatus.NEW_OFFER_REJECTED
		].includes(bet.placementStatus)
	)
);

export const selectBetsInReceipt = createSelector([selectBets], bets =>
	bets.filter(bet =>
		[
			BetPlacementStatus.DECLINED,
			BetPlacementStatus.UNKNOWN,
			BetPlacementStatus.PLACED,
			BetPlacementStatus.NEW_OFFER_REJECTED
		].includes(bet.placementStatus)
	)
);

export const selectNotDeclinedBets = createSelector([selectBets], bets =>
	bets.filter(bet =>
		[
			BetPlacementStatus.EDIT,
			BetPlacementStatus.PLACING,
			BetPlacementStatus.PLACED,
			BetPlacementStatus.NEW_OFFER_PENDING
		].includes(bet.placementStatus)
	)
);

export const selectIsPlacing = createSelector([selectBetsInPlacement], betsInPlacement => betsInPlacement.length > 0);

/**
 * Selects count of visible bets ready to be placed
 */
export const selectBetsValidToPlaceCount = createSelector([selectBetsValidToPlace], bets => bets.length);

/**
 * Selects the tab of the betslip
 */
export const selectTab = commonSelectTab;

/**
 * Selects the stage of the betslip
 */
export const selectPlacementStatus = createSelector([selectBets], bets => {
	if (
		bets.some(bet =>
			[BetPlacementStatus.NEW_OFFER_PENDING, BetPlacementStatus.PLACING].includes(bet.placementStatus)
		)
	) {
		return BetslipPlacementStatus.PLACEMENT;
	}
	if (bets.every(bet => bet.placementStatus === BetPlacementStatus.EDIT)) {
		return BetslipPlacementStatus.EDIT;
	}
	return BetslipPlacementStatus.RECEIPT;
});

/**
 * Selects the amount of items added to the betslip
 */
export const selectItemCount = dataSelectItemCount;

/**
 * Selects if the betslip is expanded or not
 */
export const selectIsExpanded = commonSelectIsExpanded;

export const selectIsSettingsOpen = commonSelectIsSettingsOpen;
export const selectIsShareOpen = commonSelectIsShareOpen;

export const selectVisibility = createSelector(
	[selectIsExpanded, selectItemCount],
	(isExpanded, itemCount): BetslipVisibility => {
		if (itemCount === 0) {
			return BetslipVisibility.HIDDEN;
		}
		return isExpanded ? BetslipVisibility.EXPANDED : BetslipVisibility.MINIMIZED;
	}
);

/**
 * Selects all active selectionIds in the betslip
 */
export const selectSelectionIds = dataSelectSelectionIds;

/**
 * Selects all pending selectionIds in the betslip
 */
export const selectPendingSelectionIds = createSelector([commonSelectPendingItems], pendingItems =>
	pendingItems.map(pendingItem => pendingItem.selection.id)
);

export const selectIsSelectionInBetslip = dataSelectIsSelectionInBetslip;

/**
 * Select all items from data
 */
export const selectItems = dataSelectItems;

/**
 * Selects total stake for all visible bets
 */
export const selectTotalStake = createSelector([selectNotDeclinedBets], bets =>
	bets.reduce((result, bet) => result + calculateBetWithdrawStake(bet), 0)
);

export const selectTotalStakeTax = createSelector([selectBets], bets =>
	bets.reduce((result, bet) => result + (bet.stakeTax || 0), 0)
);

export const selectTotalReturnsTax = createSelector([selectBets], bets =>
	bets.reduce((result, bet) => result + (bet.returnsTax || 0), 0)
);

export const selectTotalTax = createSelector(
	[selectTotalStakeTax, selectTotalReturnsTax],
	(totalStakeTax, totalReturnsTax) => totalStakeTax + totalReturnsTax
);

export const selectTotalFinalStake = createSelector(
	[selectTotalStake, selectTotalStakeTax],
	(totalStake, totalStakeTax) => totalStake - totalStakeTax
);

/**
 * Select total combined odds for all selections in betslip.
 * Missing odds counted as 1 in order to not affect odds number.
 */
export const selectTotalItemsOdds = createSelector([selectItems], items =>
	items.map(item => item?.selection?.trueOdds || 1).reduce((result, itemOdds) => multiply(result, itemOdds), 1)
);

/**
 * Select total withdraw for all visible bets. It represent amount which will be withdrawn from user balance.
 */
const selectTotalWithdraw = createSelector([selectBetsValidToPlace], bets =>
	bets.reduce((result, bet) => {
		const betBonus = bet.selectedBonus;
		// The amount we get from FreeBet bonus won't be withdraw from user balance.
		if (betBonus?.bonusType === BetBonusType.FREE_BET) {
			return result;
		}

		return result + calculateBetWithdrawStake(bet);
	}, 0)
);

/**
 * Selects total returns for all visible bets
 */
export const selectTotalReturns = createSelector([selectNotDeclinedBets], bets =>
	// Normalize returns in the same way it will be processed on the server.
	bets.reduce((result, bet) => result + (bet.finalReturns || 0), 0)
);

const selectHasStake = (state: RootState) => selectTotalStake(state) > 0;

const selectHasEnoughBalanceForStake = createSelector(
	[selectTotalWithdraw, selectBalance],
	(totalWithdraw, balance) => totalWithdraw <= 0 || totalWithdraw <= balance
);

const selectHasEnoughProductBalanceForStake = createSelector(
	[selectSportsbookValidateProductBalanceEnabled, selectTotalWithdraw, selectSportsbookBalance],
	(validateProductBalanceEnabled, totalWithdraw, sportsbookBalance) => {
		if (!validateProductBalanceEnabled) {
			return true;
		}
		return totalWithdraw <= 0 || totalWithdraw <= sportsbookBalance;
	}
);

// Odds might be different due to changes we get from data streaming.
export const selectIsBetOddsAccepted = createSelector([selectBets], bets =>
	bets.every(bet => bet.items.every(item => item.odds === item.acceptedOdds))
);

export const selectHasReplacedData = createSelector([selectItems], items => items.some(item => item.hasReplacedData));

const selectIsLoadingData = createSelector([selectItems], items => items.some(item => item.isLoading));

const selectHasCombinabilityComboRestriction = createSelector([selectBets], bets =>
	bets.some(({ errors }) => errors.some(isCombinationError))
);

export const selectBetslipBetErrors = createSelector([selectBets], bets => bets.flatMap(bet => bet.errors));
const selectHasUnavailableBet = createSelector([selectBets], bets => bets.some(bet => !isBetAvailable(bet)));

export const selectLimitations = createSelector(
	[
		selectIsAuthenticated,
		selectHasStake,
		selectHasEnoughBalanceForStake,
		selectHasEnoughProductBalanceForStake,
		selectIsBetOddsAccepted,
		selectHasReplacedData,
		selectIsLoadingData,
		selectHasCombinabilityComboRestriction,
		selectHasUnavailableBet,
		selectBetslipBetErrors,
		selectIsLocationNotShared,
		selectIsLocationPending,
		selectIsLocationDenied,
		selectIsLocationError
	],
	(
		isAuthenticated,
		hasStake,
		hasEnoughBalanceForStake,
		hasEnoughProductBalanceForStake,
		isBetOddsAccepted,
		hasReplacedData,
		isLoadingData,
		hasCombinabilityComboRestriction,
		hasUnavailableBet,
		betErrors,
		isLocationNotShared,
		isLocationPending,
		isLocationDenied,
		isLocationError
	) => {
		return Object.entries({
			[BetslipLimitation.UNAUTHORIZED_USER_ERROR]: !isAuthenticated,
			[BetslipLimitation.LOCATION_NOT_SHARED]: isLocationNotShared,
			[BetslipLimitation.LOCATION_PENDING]: isLocationPending,
			[BetslipLimitation.LOCATION_DENIED]: isLocationDenied,
			[BetslipLimitation.LOCATION_ERROR]: isLocationError,
			[BetslipLimitation.NO_STAKE_ERROR]: !hasStake,
			[BetslipLimitation.BALANCE_TOO_LOW_ERROR]: !hasEnoughBalanceForStake,
			[BetslipLimitation.PRODUCT_BALANCE_TOO_LOW_ERROR]: !hasEnoughProductBalanceForStake,
			[BetslipLimitation.BETSLIP_CHANGES_NOT_ACCEPTED]:
				!isBetOddsAccepted || hasUnavailableBet || hasReplacedData,
			[BetslipLimitation.NOT_COMBINABLE_GROUP]: hasCombinabilityComboRestriction,
			[BetslipLimitation.IS_LOADING]: isLoadingData,
			[BetslipLimitation.BET_HAS_AN_ERROR]: betErrors.length > 0
		})
			.filter(([, value]) => value)
			.map(([key]) => key as BetslipLimitation);
	}
);

/**
 * Selects whether it's possible to place bets at this moment
 */
export const selectIsPlacingBetsAllowed = createSelector(
	[selectLimitations, selectBetsValidToPlaceCount],
	(limitations, betsValidToPlaceCount) => limitations.length === 0 && betsValidToPlaceCount > 0
);

/**
 * Select option of keep selections in betslip after placement
 */
export const selectKeepSelections = commonSelectKeepSelections;

/**
 * Select current accept mode
 */
export const selectAcceptMode = commonSelectAcceptMode;

/**
 * Select a list of all selected bonus ids
 */
export const selectActiveBonusIds = createSelector([selectBets], bets =>
	bets.reduce((acc, bet) => {
		return bet.selectedBonus ? [...acc, bet.selectedBonus.givenBonusTokenId] : acc;
	}, [] as Array<number>)
);

// Helper dispatcher to check all bets on a specific tab
const checkBetsOnActiveTab = (): ThunkAction => (dispatch, getState) => {
	const bets = selectBets(getState());
	dispatch(
		checkBets(bets, () => {
			// If we should apply bonuses automatically, apply them on the first available bet.
			const shouldApplyBonus = selectShouldApplyBonus(getState());
			if (shouldApplyBonus) {
				const updatedBets = selectBets(getState());
				const appliedBonuses: Array<number> = [];
				updatedBets.forEach(bet => {
					// Find first bonus if it exists and apply it.
					const bonus = bet.bonuses.find(b => b);
					if (bonus && !appliedBonuses.includes(bonus.givenBonusTokenId)) {
						dispatch(bettingChangeSelectedBonusId(bet.ref, bonus.givenBonusTokenId));
						appliedBonuses.push(bonus.givenBonusTokenId);
					}
				});
				// If any bonuses were applied, stop applying bonuses automatically and check bets recursively.
				if (appliedBonuses.length > 0) {
					dispatch(commonChangeShouldApplyBonus(false));
					dispatch(checkBetsOnActiveTab());
				}
			}
		})
	);
};

// Helper dispatcher which ensures everything is affected correctly whenever items are altered
const dispatchItemsAction =
	(itemsAction: ThunkAction | AnyAction): ThunkAction =>
	(dispatch, getState) => {
		const maximumBetslipSystemItems = selectSportsbookMaximumBetslipSystemItems(getState());
		const itemCountBefore = dataSelectItemCount(getState());
		const tabBefore = commonSelectTab(getState());
		dispatch(itemsAction);
		dispatch(refreshBets());
		const itemCountAfter = dataSelectItemCount(getState());

		// Resubscribe always, edge case of no item changes is so rare we don't care
		dispatch(
			resubscribeToData(() => {
				dispatch(refreshBets());
				dispatch(checkBetsOnActiveTab());
			})
		);

		// Change the tab based on itemCount changes
		if (itemCountBefore < 2 && itemCountAfter >= 2) {
			dispatch(commonChangeTab(BetslipTab.COMBO));
		} else if (itemCountBefore >= 2 && itemCountAfter < 2) {
			dispatch(commonChangeTab(BetslipTab.SINGLES));
		} else if (tabBefore === BetslipTab.SYSTEM && itemCountBefore >= 3 && itemCountAfter === 2) {
			dispatch(commonChangeTab(BetslipTab.COMBO));
		} else if (tabBefore === BetslipTab.SYSTEM && itemCountAfter > maximumBetslipSystemItems) {
			dispatch(commonChangeTab(BetslipTab.COMBO));
		}

		// Expand or minimize based on itemCount changes
		if (itemCountBefore === 0 && itemCountAfter > 0) {
			dispatch(commonExpand());
		} else if (itemCountBefore > 0 && itemCountAfter === 0) {
			dispatch(commonMinimize());
		} else if (itemCountBefore === 1 && itemCountAfter > 1) {
			dispatch(commonMinimize());
		} else if (itemCountBefore > 1 && itemCountAfter === 1) {
			dispatch(commonExpand());
		}
	};

/**
 * Try to add items to the betslip. Add only unique ones, up to a maximum number of bets. Provide feedback if the limit is reached.
 */
const tryAddItems =
	(itemsToAdd: Parameters<typeof addItems>[0]): ThunkAction =>
	(dispatch, getState) => {
		if (itemsToAdd.length === 0) {
			return; // Nothing to add
		}
		const state = getState();
		const stateItems = selectItems(state);
		const existingSelectionIds = stateItems.map(item => item.selectionId);
		const uniqueItemsToAdd = itemsToAdd.filter(item => !existingSelectionIds.includes(item.selectionId));

		const maximumBetslipItems = selectSportsbookMaximumBetslipItems(state);
		const numItemsToAdd = Math.min(uniqueItemsToAdd.length, maximumBetslipItems - stateItems.length);

		if (numItemsToAdd > 0) {
			const itemsToAddLimited = uniqueItemsToAdd.slice(0, numItemsToAdd);
			dispatch(addItems(itemsToAddLimited));
		}

		if (numItemsToAdd < uniqueItemsToAdd.length) {
			dispatch(
				showQueuedNotification({
					messageKey: BetslipTextKeys.MAXIMUM_NUMBER_OF_BETSLIP_BETS_TITLE,
					color: 'neutral',
					autoHide: true,
					autoHideDuration: 5000
				})
			);
		}
	};

const toggleNormalSelections = (selections: SelectionDto[], tag: string): ThunkAction =>
	dispatchItemsAction((dispatch, getState) => {
		const items = selectItems(getState());
		const hasAllSelections = selections.every(selection => items.find(item => item.selectionId === selection.id));

		dispatch(
			hasAllSelections
				? removeItemsBySelectionIds(selections.map(s => s.id))
				: tryAddItems(selections.map(s => createItemFromSelection(s, tag)))
		);
	});

const togglePendingSelections =
	(selections: SelectionDto[], tag?: string): ThunkAction =>
	(dispatch, getState) => {
		const items = commonSelectPendingItems(getState());

		selections.forEach(selection => {
			const hasItem = items.find(item => item.selection.id === selection.id);
			dispatch(hasItem ? commonRemovePendingItems([selection.id]) : commonAddPendingItems({ selection, tag }));
		});
	};

/**
 * Toggles a single selection by either adding or removing item from the betslip
 */
export const toggleSelection =
	(selection: SelectionDto, tag: string): ThunkAction =>
	(dispatch, getState) => {
		const isPlacing = selectIsPlacing(getState());
		if (isPlacing) {
			dispatch(togglePendingSelections([selection], tag));
		} else {
			dispatch(toggleNormalSelections([selection], tag));
		}
	};

/**
 * Toggles a list of selections by either adding up until all are added or removing all from the betslip
 */
export const toggleSelections =
	(selections: SelectionDto[], tag: string): ThunkAction =>
	(dispatch, getState) => {
		const isPlacing = selectIsPlacing(getState());
		if (isPlacing) {
			dispatch(togglePendingSelections(selections, tag));
		} else {
			dispatch(toggleNormalSelections(selections, tag));
		}
	};

/**
 * Adds selections to the betslip. Does nothing if selection is already an item
 */

export const addSelections = (selections: Array<SelectionDto>, tag?: string): ThunkAction =>
	dispatchItemsAction(innerDispatch => {
		const itemsFromSelections = selections.map(sel => {
			return createItemFromSelection(sel, tag);
		});

		innerDispatch(tryAddItems(itemsFromSelections));
	});

/**
 * Removes a selection from the betslip
 */
export const removeSelection = (selectionId: string) => dispatchItemsAction(removeItemsBySelectionIds([selectionId]));

/**
 * Replaces market and selection in an existing betslip item
 */
export const replaceItemsData = (itemDataReplacements: BetslipDataItemReplacement[]) =>
	dispatchItemsAction(dataReplaceItemsData(itemDataReplacements));
/**
 * Clears all items from the betslip
 */
export const clearBetslip = () => dispatchItemsAction(clearItems());

/**
 * Accepts odds changes and removes bets/items which are unavailable. Also removes ODDS_UPDATED error from bets.
 * Responsible for removing items and bets from the betslip
 */
export const acceptChanges = (): ThunkAction => (dispatch, getState) => {
	const bets = selectBets(getState());

	const selectionIdsToRemove = [
		...new Set(
			bets.reduce((acc, bet) => {
				if (isBetAvailable(bet)) {
					return acc;
				}

				let selectionIds: Array<string> = [];

				// If bet has ITEM_UNAVAILABLE error, remove item from error.selectionIds.
				const itemUnavailableErrors = bet.errors.filter(e => e.type === BetslipBetErrorType.ITEM_UNAVAILABLE);
				if (itemUnavailableErrors.length > 0) {
					selectionIds = itemUnavailableErrors.flatMap(e => e.selectionIds || []);
				}

				// If bet has rather UNAVAILABLE or OTHER error, remove each item from bet.
				if (
					bet.errors.some(e => [BetslipBetErrorType.UNAVAILABLE, BetslipBetErrorType.OTHER].includes(e.type))
				) {
					selectionIds = [...selectionIds, ...bet.items.map(i => i.selectionId)];
				}

				if (selectionIds.length === 0) {
					return acc;
				}

				return [...acc, ...selectionIds];
			}, [] as string[])
		)
	];

	if (selectionIdsToRemove.length > 0) {
		dispatch(dispatchItemsAction(removeItemsBySelectionIds(selectionIdsToRemove)));
	}

	// Accept item odds, remove ODDS_UPDATED error from bets and check bets on active tab.
	dispatch(acceptItemChanges());
	dispatch(acceptBetsChanges());
	dispatch(checkBetsOnActiveTab());
};

/**
 * Changes the tab of the betslip
 */
export const changeTab =
	(tab: BetslipTab): ThunkAction =>
	dispatch => {
		dispatch(commonChangeTab(tab));
		dispatch(checkBetsOnActiveTab());
	};

/**
 * Changes KeepSelections option
 */
export const changeKeepSelections = commonChangeKeepSelections;

/**
 * Changes ShouldApplyBonus option
 */
export const changeShouldApplyBonus = commonChangeShouldApplyBonus;

/**
 * Changes AcceptMode
 */
export const changeAcceptMode = commonChangeAcceptMode;

/**
 * Minimizes the betslip
 */
export const minimizeBetslip = commonMinimize;

export const openSettings = commonOpenSettings;
export const closeSettings = commonCloseSettings;
export const openShare = commonOpenShare;
export const closeShare = commonCloseShare;

/**
 * Expand the betslip
 */
export const expandBetslip = commonExpand;

/**
 * Toggle the betslip
 */
export const toggleBetslip = (): ThunkAction => (dispatch, getState) => {
	const isEpxanded = commonSelectIsExpanded(getState());
	dispatch(isEpxanded ? minimizeBetslip() : expandBetslip());
};

export const toggleSettings = (): ThunkAction => (dispatch, getState) => {
	const isSettingsOpen = commonSelectIsSettingsOpen(getState());
	dispatch(isSettingsOpen ? closeSettings() : openSettings());
};

export const toggleShare = (): ThunkAction => (dispatch, getState) => {
	const isShareOpen = commonSelectIsShareOpen(getState());
	dispatch(isShareOpen ? closeShare() : openShare());
};
/**
 * Changes stake of a bet
 */
export const changeStake =
	(ref: string, stake: number): ThunkAction =>
	dispatch => {
		dispatch(bettingChangeStake(ref, stake));
		dispatch(checkBetsOnActiveTab());
	};

/**
 * Changes selected bonus id of bet. Pass undefined to unset bonus from bet.
 */
export const changeSelectedBonusId =
	(ref: string, bonusId: number | undefined): ThunkAction =>
	dispatch => {
		dispatch(bettingChangeSelectedBonusId(ref, bonusId));
		dispatch(checkBetsOnActiveTab());
	};

/**
 * Places all visible bets available to be placed
 */
export const placeBets = (): ThunkAction => (dispatch, getState) => {
	const canPlaceBets = selectIsPlacingBetsAllowed(getState());
	if (!canPlaceBets) {
		return;
	}
	unsubscribeFromData();
	const bets = selectBetsValidToPlace(getState());
	dispatch(bettingPlaceBets(bets));
};

export const acceptNewOffers =
	(betRefs: string[]): ThunkAction =>
	(dispatch, getState) => {
		const isLocationAllowed = selectIsLocationAllowed(getState());
		if (!isLocationAllowed) {
			return;
		}
		dispatch(bettingAcceptNewOffers(betRefs));
	};
export const acceptAllNewOffers = (): ThunkAction => (dispatch, getState) => {
	const isLocationAllowed = selectIsLocationAllowed(getState());
	if (!isLocationAllowed) {
		return;
	}
	dispatch(bettingAcceptAllNewOffers());
};
export const rejectNewOffers = bettingRejectNewOffers;
export const rejectAllNewOffers = bettingRejectAllNewOffers;

/**
 * Initializes the betslip session. Ensures it is booted up the correct way with the correct player settings.
 */
export const initialize =
	(
		bets?: Array<BetslipPersistedBet>,
		pendingItems?: Array<BetslipPendingItem>,
		keepSelections?: boolean
	): ThunkAction =>
	async (dispatch, getState) => {
		if ((bets && bets.length > 0) || (pendingItems && pendingItems.length > 0)) {
			const itemsFromBets = bets
				?.flatMap(bet => bet.items)
				.filter(
					(item, index, allItems) =>
						allItems.findIndex(existingItem => existingItem.selectionId === item.selectionId) === index
				);

			const itemsFromPendingItems = pendingItems
				?.map(item => {
					return createItemFromSelection(item.selection, item.tag);
				})
				.filter(
					(item, index, allItems) =>
						allItems.findIndex(existingItem => existingItem.selectionId === item.selectionId) === index
				);

			const items = [...(itemsFromBets || []), ...(itemsFromPendingItems || [])].filter(
				(item, index, allItems) =>
					allItems.findIndex(existingItem => existingItem.selectionId === item.selectionId) === index
			);

			const selectionDtos = await Api.fetchSelections(items.map(item => item.selectionId));
			const selectionIds = selectionDtos.map(selectionDto => selectionDto.id);
			const existingItems = items.filter(item => selectionIds.includes(item.selectionId));

			dispatch(
				dispatchItemsAction(innerDispatch => {
					const itemCountBefore = selectItemCount(getState());
					innerDispatch(tryAddItems(existingItems));
					const itemCountAfter = selectItemCount(getState());
					const wasOk = itemCountAfter - existingItems.length === itemCountBefore;

					if (wasOk && bets) {
						innerDispatch(replaceBets(bets));
					}
					// Nothing to do more, user already received a feedback from tryAddItems.
				})
			);
		}

		dispatch(commonInitialize());

		if (keepSelections !== undefined) {
			dispatch(changeKeepSelections(keepSelections));
		}
	};

/**
 * Dispose the betslip session. Clear state. Close all subscriptions.
 */
export const dispose = (): ThunkAction => dispatch => {
	dispatch(bettingDispose());
	dispatch(dataDispose());
	dispatch(commonDispose());
};

/**
 * Resets everything, first disposes the betslip then initializes it again
 */
export const restart = (): ThunkAction => (dispatch, getState) => {
	const keepSelections = selectKeepSelections(getState());
	const isPlacing = selectIsPlacing(getState());
	const betsInPlacement = selectBetsInPlacement(getState());
	const pendingItems = commonSelectPendingItems(getState());

	dispatch(dispose());

	if (keepSelections && isPlacing && pendingItems) {
		// Remove stake from bet
		const persistedBets = betsInPlacement.map(betToPersistedBet).map(bet => ({ ...bet, stake: 0 }));

		dispatch(initialize(persistedBets, pendingItems, undefined));
		return;
	}

	if (pendingItems) {
		dispatch(initialize(undefined, pendingItems, undefined));
		return;
	}
	dispatch(initialize());
};

// Reducer
const initialState = {
	betting: <ReturnType<typeof bettingReducer> | undefined>undefined,
	common: <ReturnType<typeof commonReducer> | undefined>undefined,
	data: <ReturnType<typeof dataReducer> | undefined>undefined
};

const reducer = (state = initialState, action: AnyAction = { type: '' }) => ({
	betting: bettingReducer(state.betting, action),
	common: commonReducer(state.common, action),
	data: dataReducer(state.data, action)
});

export default reducer;
